// ./src/components/features/CreateEventWizard/websiteBlocks.ts
//
// WHAT GOES ON THE EVENT PAGE (ROADMAP A2b #62 P6(d), owner 2026-09-30: "if user selects a sign-up list and adds items,
// no page with the content block is added on the event website so guests do not see anything").
//
// A block shows on the public site only when a page carries it — the wizard used to create sign-up items, polls and games
// that no guest could ever see. The website step (and the finish screen, for a host who finished early) offers one block
// per chosen feature plus the general ones, and puts the ticked ones on the HOME page.
//
// ⚠ It never REMOVES. A block already on any page of the site is shown as there, ticked and fixed. The HOME page's order
//   is the host's to set on the step (#62 P6(d3), owner 2026-09-30: "let user reorder the content blocks the way they
//   want") — the blocks already there and the ones being added, as one list (`homeOrder` / `orderUpdates`).
// ⚠ Default ticks (owner's picks): About, Countdown, Location map, Weather, Share, Contact us — each only when the event
//   has what it shows (a description, a start, a venue) — plus every feature block that has something to show.
// ⚠ About is an ordinary RICH-TEXT block holding the description the host writes on the step (#62 P6(d2), owner
//   2026-09-30: "for event description we need to use rich text content block"). The event's Overview (`summary`) is the
//   organizer page's one-line blurb, edited beside the organizer page on Basics.
//
// ⚠ Pure and TYPE-ONLY in its imports (`categoryOf` is passed in), so `npm run check-event-setup` runs it under Node.
import type { BlockCategory, TextBlockKind } from '@em/blocks';

import type { BlockForm } from '@/pages/private/EventPageEditorPage/blockWidgetForm';

/** The keys a wizard-made block is known by. `about` is the description's text block; the rest are their block category. */
export type WebsiteBlockKey =
  | 'about'
  | 'countdown'
  | 'tickets'
  | 'schedule'
  | 'artists'
  | 'signup'
  | 'poll'
  | 'myRole'
  | 'gameScoreboard'
  | 'prizeWinners'
  | 'pois'
  | 'accommodations'
  | 'travel'
  | 'eventMap'
  | 'volunteerShifts'
  | 'partnerPackages'
  | 'partners'
  | 'raceResults'
  | 'map'
  | 'weather'
  | 'guestbook'
  | 'contactUs'
  | 'share';

export interface WebsiteBlockOption {
  key: WebsiteBlockKey;
  /** The block category it is stored as. */
  category: BlockCategory;
  /** The event feature it shows — listed only when that feature is on. */
  feature?: string;
  /** What it needs from the event to show anything. `agenda` = the event's sessions (time slots). */
  needs?: 'description' | 'start' | 'venue' | 'sessions';
  /** An AppIcon name. */
  icon: string;
  label: string;
  description: string;
  /** The heading it gets on the page ('' = none — the block explains itself). */
  blockTitle: string;
  /**
   * The line under the heading, if any. `{{title}}` is the event's title (owner, 2026-09-30: a countdown with no heading
   * and no subtitle is a row of numbers nobody can read — counting down to WHAT?).
   */
  blockSubtitle?: string;
  /** Ticked by default when available. Feature blocks are ticked when their feature HAS something to show. */
  defaultOn: boolean;
  /**
   * ⚠ Feature data that cannot tell THIS block's content apart (partners: packages vs signed partners) — never ticked
   * by default, so a block of nothing is not published on a guess.
   */
  tickWhenData?: false;
  /**
   * What differs from the editor's blank block. ⚠⚠ The editor starts a sign-up list and a poll READ-ONLY (guests see them
   * and cannot act) — right for a host building by hand, wrong here, where the step promises "guests pick" and "guests vote".
   */
  overrides?: Partial<BlockForm>;
}

/** The catalog, IN PAGE ORDER — the order ticked blocks are added in. */
export const WEBSITE_BLOCK_OPTIONS: readonly WebsiteBlockOption[] = [
  { key: 'about', category: 'text', needs: 'description', icon: 'InfoCircle', label: 'About', description: 'Your event described in your own words — the text you write above.', blockTitle: 'About', defaultOn: true },
  { key: 'countdown', category: 'countdown', needs: 'start', icon: 'Clock', label: 'Countdown', description: 'A live countdown to the start.', blockTitle: 'The countdown is on', blockSubtitle: 'Until {{title}} begins', defaultOn: true },
  { key: 'tickets', category: 'tickets', feature: 'tickets', icon: 'Ticket', label: 'Tickets', description: 'What you sell, with prices.', blockTitle: 'Tickets', defaultOn: false },
  // ⚠ The schedule block shows the event's SESSIONS (time slots), not the Timeline feature's run-of-show — nothing on the
  //   public site shows that. So it follows the event HAVING sessions (`needs`), not the Sessions switch: a repeating
  //   event's dates are slots too, and a switch with no sessions behind it would publish an empty block.
  { key: 'schedule', category: 'schedule', needs: 'sessions', icon: 'CalendarEvent', label: 'Schedule', description: 'Your sessions — what happens when, and where.', blockTitle: 'Schedule', defaultOn: true },
  { key: 'artists', category: 'artists', feature: 'artists', icon: 'Microphone', label: 'Lineup', description: 'The acts and speakers.', blockTitle: 'Lineup', defaultOn: false },
  { key: 'signup', category: 'signup', feature: 'signup_list', icon: 'ListCheck', label: 'Sign-up list', description: 'Guests pick what they will sign up for.', blockTitle: 'Sign-up list', defaultOn: false, overrides: { signupAllowSignup: true } },
  { key: 'poll', category: 'poll', feature: 'polls', icon: 'ChartBar', label: 'Polls & quizzes', description: 'Guests vote and answer.', blockTitle: 'Polls', defaultOn: false, overrides: { pollAllowAnswer: true } },
  // ⓘ Not "role": the same block is a Human Bingo card, a Secret Santa draw, a networking match (owner, 2026-09-30).
  { key: 'myRole', category: 'myRole', feature: 'party_games', icon: 'DeviceGamepad2', label: 'Your game', description: 'Each guest plays from here — their bingo card, secret role or match.', blockTitle: 'Your game', defaultOn: false },
  { key: 'gameScoreboard', category: 'gameScoreboard', feature: 'party_games', icon: 'Trophy', label: 'Game scoreboard', description: 'Who is winning, live.', blockTitle: 'Scoreboard', defaultOn: false },
  { key: 'prizeWinners', category: 'prizeWinners', feature: 'prize_draws', icon: 'Gift', label: 'Prize winners', description: 'The draw results.', blockTitle: 'Prize winners', defaultOn: false },
  { key: 'pois', category: 'pois', feature: 'pois', icon: 'MapPin2', label: 'Places to visit', description: 'Nearby places you recommend.', blockTitle: 'Places to visit', defaultOn: false },
  { key: 'accommodations', category: 'accommodations', feature: 'accommodations', icon: 'Bed', label: 'Where to stay', description: 'Hotels and stays, with booking details.', blockTitle: 'Where to stay', defaultOn: false },
  { key: 'travel', category: 'travel', feature: 'travel', icon: 'Plane', label: 'Travel', description: 'Arrivals and departures.', blockTitle: 'Travel', defaultOn: false },
  { key: 'eventMap', category: 'eventMap', feature: 'event_map', icon: 'Map', label: 'Event map', description: 'The on-site map.', blockTitle: 'Event map', defaultOn: false },
  { key: 'volunteerShifts', category: 'volunteerShifts', feature: 'volunteers', icon: 'HeartHandshake', label: 'Volunteer', description: 'Open shifts people can apply for.', blockTitle: 'Volunteer with us', defaultOn: false },
  { key: 'partnerPackages', category: 'partnerPackages', feature: 'partners', icon: 'Briefcase', label: 'Become a partner', description: 'Sponsorship packages to apply for.', blockTitle: 'Become a partner', defaultOn: false },
  { key: 'partners', category: 'partners', feature: 'partners', icon: 'Building', label: 'Our partners', description: 'Your sponsors and partners.', blockTitle: 'Our partners', defaultOn: false, tickWhenData: false },
  { key: 'raceResults', category: 'raceResults', feature: 'race', icon: 'Run', label: 'Race results', description: 'Finish times and places.', blockTitle: 'Results', defaultOn: false },
  { key: 'map', category: 'map', needs: 'venue', icon: 'MapPin', label: 'Location map', description: 'Where it is, with the address.', blockTitle: 'Where', defaultOn: true },
  { key: 'weather', category: 'weather', needs: 'venue', icon: 'CloudRain', label: 'Weather', description: 'The forecast for the day.', blockTitle: 'Weather', defaultOn: true },
  { key: 'guestbook', category: 'guestbook', icon: 'MessageCircle', label: 'Guest book', description: 'Guests leave messages for everyone.', blockTitle: 'Guest book', defaultOn: false },
  { key: 'contactUs', category: 'contactUs', icon: 'Mail', label: 'Contact us', description: 'Guests send you a message.', blockTitle: 'Contact us', defaultOn: true },
  { key: 'share', category: 'share', icon: 'Share', label: 'Share', description: 'Buttons to share the event.', blockTitle: 'Spread the word', defaultOn: true },
];

/** What the planner needs to know about the event. */
export interface WebsitePlanInput {
  /** The event features that are on. */
  featuresOn: readonly string[];
  /** The features that already have something to show (`eventFeatureUsage.withData`). */
  withData: readonly string[];
  /** The step's description (the About block's text) is written. */
  hasDescription: boolean;
  hasStart: boolean;
  hasVenue: boolean;
  /** The event has sessions (an agenda of time slots). */
  /** The event has SESSIONS (time slots) — any mode but a repeating one, whose slots are its dates (owner, 2026-09-30). */
  hasSessions: boolean;
  /** Keys already on ANY page of the site (`websiteKeysOnSite`). */
  onSite: ReadonlySet<string>;
}

export interface WebsiteBlockRow {
  option: WebsiteBlockOption;
  /** Already on the site — shown ticked and fixed; never added again. */
  onSite: boolean;
  /** Something is missing for it to show anything (`needs`) — shown with why, and never added. */
  missing: 'description' | 'start' | 'venue' | 'sessions' | null;
  /**
   * A feature block whose feature has nothing yet. ⚠ Never added: an empty block publishes the HOST's placeholder ("Add
   * partners to this event…") to guests. It is offered again — here or on the finish screen — once there is content.
   */
  empty: boolean;
  defaultChecked: boolean;
  /**
   * Can the host tick it now? ⓘ An About block with no description yet CAN be ticked — the description is written on the
   * same step (it is added only once there is one).
   */
  tickable: boolean;
}

/**
 * The rows the step shows, in page order. A feature block is listed only when its feature is ON; it starts ticked when
 * the feature HAS something to show. A general block starts ticked by the owner's defaults, unless the event lacks what it
 * shows. Anything already on the site is ticked and fixed.
 */
export const planWebsiteBlocks = (input: WebsitePlanInput): WebsiteBlockRow[] =>
  WEBSITE_BLOCK_OPTIONS.filter((option) => !option.feature || input.featuresOn.includes(option.feature)).map((option) => {
    const onSite = input.onSite.has(option.key);
    const missing =
      option.needs === 'description' && !input.hasDescription
        ? 'description'
        : option.needs === 'start' && !input.hasStart
          ? 'start'
          : option.needs === 'venue' && !input.hasVenue
            ? 'venue'
            : option.needs === 'sessions' && !input.hasSessions
              ? 'sessions'
              : null;
    const empty = !!option.feature && !input.withData.includes(option.feature);
    const tickable = !onSite && !empty && (!missing || missing === 'description');
    // ⚠ About starts ticked even before there is a description (owner's default) — writing one on this step is enough.
    const defaultChecked = onSite || (tickable && (option.feature ? option.tickWhenData !== false : option.defaultOn));

    return { option, onSite, missing, empty, defaultChecked, tickable };
  });

/**
 * The website keys a set of widgets already carries. `about` is the text block THIS wizard made for the description
 * (`aboutWidgetId`, kept in the draft — an ordinary text block carries no mark of its own), or an older live Overview
 * block; every other key is its block category (via `categoryOf`, the renderer's own rule, passed in so this file stays
 * pure). ⚠ Any other text block is the host's own and is never taken for the About.
 */
export const websiteKeysOnSite = <W extends { id?: string | null; widgetType?: string | null; config?: { textSource?: string | null } | null }>(
  widgets: readonly W[],
  categoryOf: (widget: W) => string,
  aboutWidgetId?: string | null,
): Set<string> => {
  const keys = new Set<string>();
  const known = new Set<string>(WEBSITE_BLOCK_OPTIONS.map((o) => o.key));

  for (const w of widgets) {
    if (w?.config?.textSource === 'eventSummary' || (!!aboutWidgetId && w?.id === aboutWidgetId)) {
      keys.add('about');
      continue;
    }

    // ⚠ A key IS its block's category (bar `about`) — which is what lets the renderer's own rule name the block here.
    const category = categoryOf(w);

    if (known.has(category)) {
      keys.add(category);
    }
  }

  return keys;
};

/**
 * What to ADD for these ticked keys: in page order, never one already on the site, never one the event cannot show
 * (a feature that is off, a map with no venue…).
 */
export const websiteKeysToAdd = (rows: readonly WebsiteBlockRow[], ticked: readonly string[]): WebsiteBlockKey[] =>
  rows.filter((r) => ticked.includes(r.option.key) && !r.onSite && !r.missing && !r.empty).map((r) => r.option.key);

/**
 * The FEATURES whose content is not on the site yet — for the finish screen's "your sign-up list isn't on your event page
 * yet". Only features with something to show, only blocks the step would tick itself, and never one the host saw on the
 * website step and left unticked (`declined`) — that was a choice, not an oversight.
 */
export const featureBlocksMissing = (rows: readonly WebsiteBlockRow[], declined: readonly string[] = []): WebsiteBlockRow[] =>
  rows.filter((r) => !!r.option.feature && r.defaultChecked && !r.onSite && !declined.includes(r.option.key));

// ─── #62 P6(d3) — THE HOME PAGE'S ORDER ─────────────────────────────────────────────────────────────────────────────

/** A block on the home-page order list: one already there (`w:<widget id>`) or one being added (`k:<key>`). */
export const orderItemOfWidget = (widgetId: string): string => `w:${widgetId}`;
export const orderItemOfKey = (key: WebsiteBlockKey): string => `k:${key}`;

/**
 * The home page's order as the step shows it: the host's own order (`saved`) for every item still there, then anything
 * not in it — blocks already on the page in their page order, then the blocks being added in catalog order.
 * ⓘ An item the host ordered that is gone (unticked, or removed in the editor since) simply drops out.
 */
export const homeOrder = (
  existingIds: readonly string[],
  addingKeys: readonly WebsiteBlockKey[],
  saved: readonly string[] | null | undefined,
): string[] => {
  const natural = [...existingIds.map(orderItemOfWidget), ...addingKeys.map(orderItemOfKey)];
  const available = new Set(natural);
  const kept = (saved ?? []).filter((item, index, all) => available.has(item) && all.indexOf(item) === index);
  const placed = new Set(kept);

  return [...kept, ...natural.filter((item) => !placed.has(item))];
};

/** The gap between blocks when a page is renumbered (the editor's own rebalance uses the same step). */
export const ORDER_STEP = 1000;

/**
 * The `orderNo` writes that put these widgets in this order: 1000, 2000, … — and ONLY the rows whose number changes, so
 * an untouched order writes nothing.
 * ⚠ `ordered` must be the WHOLE page (every block on it, live and draft): renumbering part of a page can land a block
 *   between two it was never next to.
 */
export const orderUpdates = (
  ordered: readonly string[],
  current: ReadonlyArray<{ id?: string | null; orderNo?: number | string | null }>,
): Array<{ id: string; orderNo: number }> => {
  const now = new Map(current.filter((w) => !!w.id).map((w) => [String(w.id), Number(w.orderNo)]));

  return ordered
    .map((id, index) => ({ id, orderNo: (index + 1) * ORDER_STEP }))
    .filter(({ id, orderNo }) => now.get(id) !== orderNo);
};

// ─── #62 P6(d2) — THE DESCRIPTION'S FORMAT ──────────────────────────────────────────────────────────────────────────
// The step edits with `PolicyTextEditor` (formats: richtext | markdown | html | plain — POLICY_CONTENT_FORMAT) and saves a
// text block (kinds: richtext | markdown | html | text). The same four, two spellings of "plain".

/** The text block kind a description written in this editor format is saved as. Unknown → rich text (the default). */
export const textKindOfFormat = (format: string | null | undefined): TextBlockKind =>
  format === 'plain' ? 'text' : format === 'markdown' || format === 'html' ? format : 'richtext';

/** The editor format a text block of this kind is edited in. */
export const formatOfTextKind = (kind: TextBlockKind | null | undefined): string =>
  kind === 'text' ? 'plain' : kind === 'markdown' || kind === 'html' ? kind : 'richtext';

/**
 * The host's order with the blocks made since written as themselves (`k:<key>` → `w:<id>`) — kept in the draft after a
 * save that failed half way, so the retry PLACES them where the host put them instead of dropping them to the end.
 */
export const orderAfterCreate = (order: readonly string[] | null | undefined, created: Partial<Record<string, string>>): string[] | null =>
  order ? order.map((item) => (item.startsWith('k:') && created[item.slice(2)] ? orderItemOfWidget(created[item.slice(2)] as string) : item)) : null;
